import type { EnumAssemblyType } from "@/app/services/generated/enums/enumAssemblyType.generated";
import { translateWithParams, useI18n } from "@/plugins/i18n";
import type { Location } from "vue-router";
import { routes } from "@/app/ui/navigation/menuItems";
import type Guid from "@/app/types/common/guid";
import type { Ref } from "vue";
import { computed } from "vue";
import type { EnumAssemblyStatus } from "@/app/services/generated/enums/enumAssemblyStatus.generated";
import type Key from "@/app/types/common/key";
import type { EnumOutputMovementType } from "@/components/pages/production/assemblies/composables/enumOutputMovementType";

/**
 * Provides the variation between the different types of recipes.
 *
 * Purposely specifies labels and routes explicitly rather than using runtime
 * construction of the labels/routes. This ensures the routes and labels are defined
 * at compile time by the typescript compiler.
 */

export interface RecipeLabels {
  readonly listPageTitle: string;
  readonly recipeNoun: string;
  readonly createAssemblyKey: Key;
  readonly createRecipeKey: Key;
  readonly deleteConfirmation: string;

  // Copy
  readonly copyDialogTitle: string;
  readonly copyToKey: Key;
  readonly copyNewRecipeNameKey: Key;

  // Grid columns
  readonly recipeNameKey: Key;
  readonly outputStockItemNameKey: Key;
  readonly outputQuantityKey: Key;

  // Grid Actions
  readonly viewRecipeKey: Key;
  readonly editRecipeKey: Key;
  readonly copyRecipeKey: Key;
  readonly startRecipeKey: Key;
  readonly deleteRecipeKey: Key;
  readonly hideRecipeKey: Key;
  readonly unhideRecipeKey: Key;

  readonly editPageTitle: string;
  readonly editPageDescription: string;
  readonly ingredientProductsKey: Key;
  readonly ingredientMaterialsKey: Key;
  readonly editPageRecipeNameKey: Key;
  readonly editPageQuantityProducedKey: Key;
  readonly ingredientSectionHeader: string;
}

export function getRecipeLabels(assemblyType: EnumAssemblyType): RecipeLabels {
  const { $m } = useI18n();

  if (assemblyType.isMaterialProduction()) {
    return {
      listPageTitle: $m.production_recipe_list,
      recipeNoun: $m.production_recipe_noun,
      createAssemblyKey: $m.keys.production_recipe_assembly_create,
      createRecipeKey: $m.keys.production_recipe_create,
      deleteConfirmation: $m.production_recipe_delete_confirmation,
      copyDialogTitle: $m.production_recipe_copy,
      copyToKey: $m.keys.production_recipe_copy_to_recipe,
      copyNewRecipeNameKey: $m.keys.production_recipe_new_recipe_name,
      recipeNameKey: $m.keys.production_recipe_name,
      outputStockItemNameKey: $m.keys.production_recipe_product,
      outputQuantityKey: $m.keys.production_recipe_amount_produced,
      viewRecipeKey: $m.keys.production_recipe_view,
      editRecipeKey: $m.keys.production_recipe_edit,
      copyRecipeKey: $m.keys.production_recipe_copy,
      startRecipeKey: $m.keys.production_recipe_start,
      deleteRecipeKey: $m.keys.production_recipe_delete,
      hideRecipeKey: $m.keys.production_recipe_hide,
      unhideRecipeKey: $m.keys.production_recipe_unhide,
      editPageTitle: $m.production_recipe_new_recipe,
      editPageDescription: $m.production_recipe_description,
      ingredientProductsKey: $m.keys.production_recipe_product,
      ingredientMaterialsKey: $m.keys.production_recipe_material,
      editPageRecipeNameKey: $m.keys.production_recipe_name,
      editPageQuantityProducedKey: $m.keys.production_recipe_quantity_produced,
      ingredientSectionHeader: $m.production_recipe_ingredients,
    };
  }

  return {
    listPageTitle: $m.production_packaging_list,
    recipeNoun: $m.production_packaging_noun,
    createAssemblyKey: $m.keys.production_packaging_assembly_create,
    createRecipeKey: $m.keys.production_packaging_create,
    deleteConfirmation: $m.production_packaging_delete_confirmation,
    copyDialogTitle: $m.production_packaging_copy,
    copyToKey: $m.keys.production_packaging_copy_to_packaging,
    copyNewRecipeNameKey: $m.keys.production_packaging_new_recipe_name,
    recipeNameKey: $m.keys.production_packaging_name,
    outputStockItemNameKey: $m.keys.production_packaging_product,
    outputQuantityKey: $m.keys.production_packaging_amount_produced,
    viewRecipeKey: $m.keys.production_packaging_view,
    editRecipeKey: $m.keys.production_packaging_edit,
    copyRecipeKey: $m.keys.production_packaging_copy,
    startRecipeKey: $m.keys.production_packaging_start,
    deleteRecipeKey: $m.keys.production_packaging_delete,
    hideRecipeKey: $m.keys.production_packaging_hide,
    unhideRecipeKey: $m.keys.production_packaging_unhide,
    editPageTitle: $m.production_packaging_new_recipe,
    editPageDescription: $m.production_packaging_description,
    ingredientProductsKey: $m.keys.production_packaging_product,
    ingredientMaterialsKey: $m.keys.production_recipe_material,
    editPageRecipeNameKey: $m.keys.production_packaging_name,
    editPageQuantityProducedKey: $m.keys.production_packaging_quantity_produced,
    ingredientSectionHeader: $m.production_packaging_ingredients,
  };
}
export interface RecipeRoutes {
  getRecipeListPage(): Location;
  getCreateRecipeRoute(): Location;
  getViewRecipeRoute(id: Guid): Location;
  getEditRecipeRoute(id: Guid): Location;
}

export function getRecipeRoutes(assemblyType: EnumAssemblyType): RecipeRoutes {
  if (assemblyType.isMaterialProduction()) {
    return {
      getRecipeListPage: () => ({ name: routes.productionMenu.recipe.list }),
      getCreateRecipeRoute: () => ({
        name: routes.productionMenu.recipe.create,
      }),
      getViewRecipeRoute: (id) => ({
        name: routes.productionMenu.recipe.view,
        params: { id: id.toString() },
      }),
      getEditRecipeRoute: (id) => ({
        name: routes.productionMenu.recipe.edit,
        params: { id: id.toString(), defaultEditing: "true" },
      }),
    };
  }

  return {
    getRecipeListPage: () => ({ name: routes.productionMenu.packaging.list }),
    getCreateRecipeRoute: () => ({
      name: routes.productionMenu.packaging.create,
    }),
    getViewRecipeRoute: (id) => ({
      name: routes.productionMenu.packaging.view,
      params: { id: id.toString() },
    }),
    getEditRecipeRoute: (id) => ({
      name: routes.productionMenu.packaging.edit,
      params: { id: id.toString(), defaultEditing: "true" },
    }),
  };
}

export interface AssemblyLabels {
  readonly assemblyOutputItemKey: Key;
  readonly assemblyTypeKey: Key;
  readonly remainingExpectedOutputKey: Key;
  readonly progressAssemblyButtonKey: Key;
  readonly expectedFinishDateKey: Key;
  readonly assemblyOutputKey: Key;
  readonly assemblyFinishDateKey: Key;
}

export function getAssemblyLabels(
  assemblyType: EnumAssemblyType,
  assemblyStatus: EnumAssemblyStatus
): AssemblyLabels {
  const { $m } = useI18n();

  return {
    assemblyOutputItemKey: assemblyType.isMaterialProduction()
      ? $m.keys.assembly_output_material
      : $m.keys.assembly_output_product,
    assemblyTypeKey: assemblyType.isMaterialProduction()
      ? $m.keys.assembly_recipe
      : $m.keys.assembly_packaging,
    remainingExpectedOutputKey: assemblyStatus.isInProgress()
      ? $m.keys.assembly_remaining_expected_output
      : $m.keys.assembly_expected_output,
    progressAssemblyButtonKey: assemblyStatus.isOpen()
      ? $m.keys.assembly_start
      : assemblyType.isMaterialProduction()
        ? $m.keys.assembly_complete_batch
        : $m.keys.assembly_complete_packaging,
    expectedFinishDateKey: assemblyStatus.isComplete()
      ? $m.keys.assembly_date_finished
      : $m.keys.assembly_expected_finish_date,
    assemblyOutputKey: assemblyStatus.isComplete()
      ? $m.keys.assembly_actual_output
      : $m.keys.assembly_current_output,
    assemblyFinishDateKey: assemblyStatus.isComplete()
      ? $m.keys.assembly_date_finished
      : $m.keys.assembly_expected_finish_date,
  };
}

export interface AssemblyRoutes {
  getEditAssemblyRoute(id: Guid): Location;
  getCloseAssemblyRoute(): Location;
}

export function getAssemblyRoutes(
  assemblyType: EnumAssemblyType,
  assemblyStatus: EnumAssemblyStatus
): AssemblyRoutes {
  const editAssemblyRouteName = assemblyType.isMaterialProduction()
    ? routes.productionMenu.batch.view
    : routes.productionMenu.assembly.view;

  return {
    getEditAssemblyRoute: (id) => ({
      name: editAssemblyRouteName,
      params: { id: id.toString() },
    }),
    getCloseAssemblyRoute: () => ({
      name: assemblyStatus.isComplete()
        ? routes.productionMenu.complete
        : assemblyStatus.isInProgress()
          ? routes.productionMenu.inProgress
          : routes.productionMenu.planned,
    }),
  };
}

/**
 * Reactive provider for the labels and routes information.
 */
export function useAssemblyTypeVariations(
  assemblyType: Ref<EnumAssemblyType>,
  assemblyStatus: Ref<EnumAssemblyStatus>
) {
  return {
    assemblyLabels: computed<AssemblyLabels>(() =>
      getAssemblyLabels(assemblyType.value, assemblyStatus.value)
    ),
    assemblyRoutes: computed<AssemblyRoutes>(() =>
      getAssemblyRoutes(assemblyType.value, assemblyStatus.value)
    ),
  };
}

export interface AssemblyOutputLabels {
  readonly quantityToMoveKey: Key;
  readonly assemblySelectorKey: Key;
  readonly addToOrCreateButton: string; // Not a key as we inject our assembly type
  readonly outputItemKey: Key;
  readonly multiplyIngredients: string; // Not a key as we inject our assembly type
  readonly newAssemblyOutputItemKey: Key;
  readonly newAssemblyOutputRecipeKey: Key;
}

export function getAssemblyOutputLabels(
  movementType: EnumOutputMovementType,
  shouldAddToExisting: boolean
): AssemblyOutputLabels {
  const { $m } = useI18n();

  return {
    quantityToMoveKey: movementType.isMoveToBatch()
      ? $m.keys.assembly_quantity_to_move_to_batch
      : movementType.isPackage()
        ? $m.keys.assembly_quantity_to_package
        : $m.keys.assembly_quantity_to_move_to_stock,
    assemblySelectorKey: movementType.isMoveToBatch()
      ? $m.keys.assembly_batch_batch_number_product
      : $m.keys.assembly_batch_number_product,
    addToOrCreateButton: shouldAddToExisting
      ? translateWithParams("assembly_create_new", {
          type: movementType.isMoveToBatch()
            ? $m.assembly_batch
            : $m.assembly_packaging,
        })
      : $m.assembly_add_to_existing_assembly,
    outputItemKey: movementType.isMoveToBatch()
      ? $m.keys.assembly_output_material
      : $m.keys.assembly_output_product,
    multiplyIngredients: translateWithParams(
      "assembly_multiple_ingredients_by_expected",
      {
        type: movementType.isMoveToBatch()
          ? $m.assembly_batch
          : $m.assembly_packaging,
      }
    ),
    newAssemblyOutputItemKey: movementType.isMoveToBatch()
      ? $m.keys.assembly_new_output_material
      : $m.keys.assembly_new_output_product,
    newAssemblyOutputRecipeKey: movementType.isMoveToBatch()
      ? $m.keys.assembly_recipe
      : $m.keys.assembly_packaging,
  };
}

/**
 * Reactive provider for the output labels
 */
export function useAssemblyOutputTypeVariations(
  movementType: Ref<EnumOutputMovementType>,
  shouldAddToExisting: Ref<boolean>
) {
  return {
    outputLabels: computed<AssemblyOutputLabels>(() =>
      getAssemblyOutputLabels(movementType.value, shouldAddToExisting.value)
    ),
  };
}
